import { t } from '@lingui/macro';
import { GridApi } from 'ag-grid-community';
import { format } from 'date-fns';
import _ from 'lodash';
import {
  LOCAL_STORAGE_KEY_SETTINGS,
  TApplicationTableStateContextValue,
} from 'contexts/ApplicationTableStateContext/ApplicationTableStateContext';
import { TDateUnit, TFormalOffer } from 'lendingApi/applications.types';
import { TAvailableProducts } from 'lendingApi/lendingApi.types';
import { TApplicationStatus } from 'types';
import {
  ALL_STATUS_VALUES,
  APPLICATION_TABLE_KEYS,
  PRODUCT_DISPLAY_ORDER,
  PRODUCT_NAMES,
} from 'utils/applications/applications.config';
import { getDurationInMonths } from 'utils/durationHelper';

export const applicationStatuses = {
  declined: t`App declined`,
  deferred: t`App deferred`,
  offered: t`Offer generated`,
  conditional_offer: t`Conditional offer generated`,
  open: t`Ongoing`,
  funded: t`Funded`,
  expired: t`Offer expired`,
  talking_to_customer: t`Applicant funding steps`,
  in_underwriting: t`In review by underwriter`,
  manual_review: t`Manual check`,
  awaiting_underwriter: t`Awaiting underwriter`,
  action_required: t`Docs required`,
  offer_revoked: t`Offer revoked`,
  customer_to_link_open_banking: t`Client to link bank account`,
  offer_rejected: t`Offer rejected`,
};

export function getApplicationStatus(status: TApplicationStatus) {
  if (applicationStatuses[status]) {
    return applicationStatuses[status];
  }
  return 'Ongoing';
}

/**
 * Product utility to help derive name and type based on locale and duration.
 */

type TDeriveProductFromTypeAndDuration = {
  productType?: TAvailableProducts;
  duration?: { amount: number; unit: TDateUnit } | null;
  isUK: boolean;
};

type TReturnDeriveProduct = {
  name: string;
  type: TAvailableProducts | null;
};

export function deriveProductFromTypeAndDuration({
  productType,
  duration,
  isUK,
}: TDeriveProductFromTypeAndDuration): TReturnDeriveProduct {
  if (!productType) return { name: '-', type: null };

  const durationInMonths = getDurationInMonths(duration);
  if (productType === 'credit_facility') {
    if (isUK && durationInMonths === 60) {
      return { name: PRODUCT_NAMES.term_loan, type: 'term_loan' };
    }
    if (!isUK && durationInMonths === 24) {
      return { name: PRODUCT_NAMES.flexi24, type: 'flexi24' };
    }
  }

  if (PRODUCT_NAMES[productType]) {
    return { name: PRODUCT_NAMES[productType], type: productType };
  }

  return { name: '', type: null };
}

export function generateDateMonthsPrevious(months: number | null) {
  if (months === null) {
    return null;
  }

  const currentDate = new Date();
  const monthsDateFrom = new Date(
    currentDate.setMonth(currentDate.getMonth() - months),
  );
  const formattedDate = format(monthsDateFrom, 'yyyy-MM-dd');

  return formattedDate;
}

export function isCrossSellProduct(
  formal_offer_product: string,
  requested_product?: string | null,
) {
  if (!requested_product) return false;

  if (
    formal_offer_product === 'credit_facility' &&
    requested_product === 'recovery_loan'
  ) {
    return true;
  }

  return false;
}

/**
 * Reorder the selected fields to match the cred_facility,flexi-24,term_loan arrangement.
 * To ensure offers are displayed in a consistent order that also matches the filters check
 */
function sortProductNames(productNames: string[]): string[] {
  const orderedArr = PRODUCT_DISPLAY_ORDER.filter((product) =>
    productNames.includes(product),
  );
  const remainingArr = productNames.filter(
    (product) => !PRODUCT_DISPLAY_ORDER.includes(product),
  );
  return Array.from(new Set([...orderedArr, ...remainingArr]));
}

export function getProductNames(data: any, isUK: boolean) {
  const formalOffers = data?.formal_offer_sets[0]?.formal_offers;
  const hasMultipleFormalOffers = formalOffers?.length > 1;

  const selectedOffer = formalOffers?.find((offer: TFormalOffer) => {
    return offer.is_selected;
  });

  const duration = data?.requested_products[0]?.duration; // Product type can be intentionally undefined to show the dash (-) instead

  if (hasMultipleFormalOffers && !selectedOffer) {
    return sortProductNames(
      formalOffers.map(
        ({ product_type, duration: formalOfferDuration }: TFormalOffer) =>
          deriveProductFromTypeAndDuration({
            productType: product_type,
            duration: formalOfferDuration,
            isUK,
          }).name,
      ),
    ).join(',');
  } else {
    let productType;

    if (selectedOffer) {
      productType = selectedOffer?.product_type;
    } else if (formalOffers?.[0]?.product_type) {
      productType = formalOffers[0].product_type;
    } else {
      productType = data?.requested_products?.[0]?.product_type;
    }

    return deriveProductFromTypeAndDuration({
      productType,
      duration,
      isUK,
    }).name;
  }
}

export const getDurations = (data: any) => {
  const formalOffers = data?.formal_offer_sets[0]?.formal_offers;
  const hasMultipleFormalOffers = formalOffers?.length > 1;

  const selectedOffer = formalOffers?.find((offer: TFormalOffer) => {
    return offer.is_selected;
  });

  if (hasMultipleFormalOffers && !selectedOffer) {
    return formalOffers
      .map(({ duration: formalOfferDuration }: TFormalOffer) => {
        const numberOfMonths = getDurationInMonths(formalOfferDuration);
        return numberOfMonths === 0 ? '-' : `${numberOfMonths} months`;
      })
      .join(',');
  } else {
    let duration;

    if (selectedOffer) {
      duration = selectedOffer?.duration;
    } else if (formalOffers?.[0]?.duration) {
      duration = formalOffers[0].duration;
    } else {
      duration = data?.requested_products?.[0]?.duration;
    }

    const numberOfMonths = getDurationInMonths(duration);

    return duration && Boolean(numberOfMonths)
      ? `${numberOfMonths} months`
      : '-';
  }
};

export const bindListFilters = (
  gridApi: GridApi,
  {
    getFilterValues,
  }: Pick<TApplicationTableStateContextValue, 'getFilterValues'>,
) => {
  for (const tableKey of [
    APPLICATION_TABLE_KEYS.status,
    APPLICATION_TABLE_KEYS.product_type,
    APPLICATION_TABLE_KEYS.term_length,
  ]) {
    const tableKeyFilters = getFilterValues(tableKey);

    // Setting value to 'tableKeyFilters || ALL_STATUS_VALUES' here incase 'All' was selected in the external filters
    if (tableKey === APPLICATION_TABLE_KEYS.status) {
      gridApi.getFilterInstance(APPLICATION_TABLE_KEYS.status)?.setModel({
        value: tableKeyFilters || ALL_STATUS_VALUES,
      });
    } else if (tableKeyFilters) {
      gridApi.getFilterInstance(tableKey)?.setModel({
        value: tableKeyFilters,
      });
    }
    gridApi.onFilterChanged();
  }
};

export const registerNativeAgGridFilters = (
  gridApi: GridApi,
  {
    saveFilter,
    removeFilter,
    getFilterValues,
  }: Pick<
    TApplicationTableStateContextValue,
    'saveFilter' | 'removeFilter' | 'getFilterValues'
  >,
) => {
  Object.values(APPLICATION_TABLE_KEYS).forEach((key) => {
    if (
      key !== APPLICATION_TABLE_KEYS.status &&
      key !== APPLICATION_TABLE_KEYS.product_type &&
      key !== APPLICATION_TABLE_KEYS.term_length
    ) {
      const currentFilter = gridApi.getFilterInstance(key)?.getModel();
      const existingFilter = getFilterValues(key);

      if (!_.isEqual(existingFilter || {}, currentFilter || {})) {
        if (currentFilter) {
          saveFilter(key, currentFilter);
        } else {
          removeFilter(key);
        }
      }
    }
  });
  gridApi.onFilterChanged();
};

export const DATE_RANGE_NUMERIC_VALUES: { [key: string]: number | null } = {
  '1 month': 1,
  '3 months': 3,
  '6 months': 6,
  'all months': null,
};

export const getDefaultDateRangeNumber = () => {
  const storedSettings = JSON.parse(
    localStorage?.getItem(LOCAL_STORAGE_KEY_SETTINGS) || '{}',
  );
  const defaultDateFromNumber = storedSettings?.dateRange
    ? DATE_RANGE_NUMERIC_VALUES[storedSettings.dateRange]
    : 1;

  return defaultDateFromNumber || null;
};
